<template>
  <div class="modal is-active">
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <div class="media">
          <div class="media-left">
            <figure class="image is-48x48">
              <svg class="app-icon">
                <use xmlns:xlink="http://www.w3.org/1999/xlink" :xlink:href="`#${currentApp.icon || ''}`"></use>
              </svg>
            </figure>
          </div>
          <div class="media-content">
            <p class="title is-4">
              <strong>{{ currentApp.name }}</strong
              ><n-badge variant="secondary" v-if="hasStatusBadge()" class="status-badge">{{
                getStatusBadgeText()
              }}</n-badge>
            </p>
            <p class="subtitle is-6">{{ currentApp.category }}</p>
          </div>
        </div>
        <button class="delete" aria-label="close" @click="showAppsStore()"></button>
      </header>
      <section class="modal-card-body">
        <div class="columns">
          <div class="column main-content-view">
            <div class="tabs" v-if="currentApp.isInstalled && currentApp.requiresInstallConfig">
              <ul>
                <li :class="{ 'is-active': isActiveTab('Overview') }" @click="showAppOverview()"><a>Overview</a></li>
                <li :class="{ 'is-active': isActiveTab('Config') }" @click="showAppConfig()"><a>Config</a></li>
              </ul>
            </div>
            <router-view></router-view>
          </div>
          <div class="column is-one-fifth">
            <div class="container install-actions">
              <n-button
                v-if="!currentApp.isInstalled"
                @click="installApp()"
                variant="success"
                :disabled="!canInstall()"
                :loading="actionInProgress"
              >
                Install
              </n-button>
              <n-button
                v-if="currentApp.isInstalled"
                @click="uninstallApp()"
                variant="danger"
                :loading="actionInProgress"
              >
                Uninstall
              </n-button>
            </div>
            <div class="container app-links">
              <h6 class="title is-6">Related Links</h6>
              <p v-if="currentApp.websiteUrl">
                <a :href="currentApp.websiteUrl" target="_blank">Website</a>
              </p>
              <p v-if="currentApp.privacyPolicyUrl">
                <a :href="currentApp.privacyPolicyUrl" target="_blank">Privacy Policy</a>
              </p>
              <p v-if="currentApp.termsOfServiceUrl">
                <a :href="currentApp.termsOfServiceUrl" target="_blank">Terms of Service</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
    <AppUninstallNotAllowedModal
      v-if="showAppUninstallNotAllowedModal"
      :message="uninstallNotAllowedMessage"
      @cancel="showAppUninstallNotAllowedModal = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { InstallAppViewModel } from '../models';
import { appsApi } from '../services/apps';
import { AppApiFactory } from '../services/appApiFactory';
import Toast from '@/shared/services/toast';
import { GET_CHANNEL_IDENTITIES } from '@/store/channel-identities/actionTypes';
import AppUninstallNotAllowedModal from './AppUninstallNotAllowedModal.vue';
import appInsights from '@/shared/services/appInsights';
import { ADD_INSTALLED_APP, REMOVE_INSTALLED_APP } from '@/store/app/mutationTypes';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import { ChannelIdentityViewModel } from '@/messaging/models/channelIdentityViewModel';
import { useRouter } from 'vue-router';

const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const currentApp = computed(() => store.getters.currentApp);
const isWorkspaceFeatureEnabled = (feature: string) => computed(() => store.getters.isWorkspaceFeatureEnabled(feature));
const addInstalledApp = (appId: string) => store.commit(ADD_INSTALLED_APP, appId);
const removeInstalledApp = (appId: string) => store.commit(REMOVE_INSTALLED_APP, appId);
const loadChannelIdentities = (forceReload = false): Promise<ChannelIdentityViewModel[]> =>
  store.dispatch(GET_CHANNEL_IDENTITIES, forceReload);

const tabs = ref<string[]>([]);
const activeTab = ref('');
const actionInProgress = ref(false);
const showAppUninstallNotAllowedModal = ref(false);

const uninstallNotAllowedMessage = computed(() => {
  const specificAppApi = AppApiFactory.get(currentApp.value.id);
  return specificAppApi.getUninstallNotAllowedMessage();
});

const eventProperties = computed(() => {
  return {
    appId: currentApp.value.id,
    workspaceId: currentWorkspace.value.id,
  };
});

onBeforeMount(() => {
  if (currentApp.value == null) {
    showAppsStore();
    return;
  }

  if (currentApp.value.isInstalled && currentApp.value.requiresInstallConfig) {
    showAppConfig();
    return;
  }

  showAppOverview();
});

function isActiveTab(tab: string): boolean {
  return activeTab.value === tab;
}

function canInstall(): boolean {
  if (isAppLive()) {
    return true;
  }

  if (isWorkspaceFeatureEnabled(`app_${currentApp.value.id}`)) {
    return true;
  }

  return false;
}

function hasStatusBadge(): boolean {
  return !isAppLive();
}

function getStatusBadgeText(): string {
  if (!isAppLive()) {
    return 'Coming Soon!';
  }

  return '';
}

function installApp(): void {
  appInsights.trackEvent({ name: 'App install clicked' }, eventProperties.value);
  if (!canInstall()) {
    return;
  }

  if (actionInProgress.value) {
    return;
  }

  actionInProgress.value = true;

  const req = {
    appId: currentApp.value.id,
    published: true,
  } as InstallAppViewModel;

  appsApi
    .install(currentWorkspace.value.id, req)
    .then((result) => {
      appInsights.trackEvent({ name: 'App installed' }, eventProperties.value);

      // update apps list
      currentApp.value.isInstalled = true;
      currentApp.value.installedAppId = result.installedAppId;
      if (currentApp.value.requiresInstallConfig) {
        showAppConfig();
      }
      addInstalledApp(currentApp.value.id);
    })
    .finally(() => {
      actionInProgress.value = false;
    });
}

function uninstallApp(): void {
  appInsights.trackEvent({ name: 'App uninstall clicked' }, eventProperties.value);
  if (actionInProgress.value) {
    return;
  }

  actionInProgress.value = true;

  const specificAppApi = AppApiFactory.get(currentApp.value.id);

  if (specificAppApi && !specificAppApi.canUninstall()) {
    showAppUninstallNotAllowedModal.value = true;
    actionInProgress.value = false;
    return;
  }

  let uninstallPromise: Promise<void>;

  if (specificAppApi) {
    uninstallPromise = specificAppApi.uninstall(currentWorkspace.value.id, currentApp.value.installedAppId).then(() => {
      return appsApi.uninstall(currentWorkspace.value.id, currentApp.value.installedAppId);
    });
  } else {
    uninstallPromise = appsApi.uninstall(currentWorkspace.value.id, currentApp.value.installedAppId);
  }

  uninstallPromise // either one or both of the above two promises
    .then(() => {
      appInsights.trackEvent({ name: 'App uninstalled' }, eventProperties.value);

      // update apps list
      currentApp.value.isInstalled = false;
      currentApp.value.installedAppId = '';
      showAppOverview();
      loadChannelIdentities(true);
      removeInstalledApp(currentApp.value.id);
    })
    .catch((err) => {
      Toast.showError(err.message);
    })
    .finally(() => {
      actionInProgress.value = false;
    });
}

function showAppsStore(): void {
  router.push({ name: 'apps' });
}

function showAppOverview(): void {
  activeTab.value = 'Overview';
  router.push({ name: 'app-overview' });
}

async function showAppConfig() {
  activeTab.value = 'Config';
  try {
    await router.push({ name: 'app-config', params: { id: currentApp.value.id } });
  } catch (error: any) {
    console.error('Failed to push to app-config', error);
  }
}

function isAppLive(): boolean {
  return !currentApp.value.status || currentApp.value.status === 'live';
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.modal-card-head {
  .delete {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
  }
}

.columns {
  flex: 1;
}

.column:first-of-type {
  border-right: 1px solid #dbdbdb;
}

.modal-card {
  width: 90%;
  max-width: 1000px;
  height: 70%;
}

.modal-card-body {
  display: flex;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

.install-actions {
  margin-bottom: 2rem;

  .btn {
    width: 100%;
  }
}

.app-links {
  .title {
    margin-bottom: 1rem;
  }
}

.app-icon {
  width: 3rem;
  height: 3rem;
}

.main-content-view {
  overflow-y: auto;
}

.status-badge {
  margin-left: 1em;
  vertical-align: middle;
}

.badge.badge--secondary.status-badge {
  background-color: #ffe08a;
}
</style>
